import type { Breakpoint, TileConfig, TileItem, WidgetType } from "@csbi/contracts"
import { defaultSplitFor, type MetricDefinition } from "@csbi/metric-catalog"
import { COLS, type GridItem, type Layouts } from "./grid.js"

export const WIDGET_SIZE: Record<WidgetType, { w: number; h: number }> = {
  "stat-tile": { w: 3, h: 3 },
  bar: { w: 6, h: 7 },
  hbar: { w: 6, h: 8 },
  "stacked-bar": { w: 6, h: 7 },
  line: { w: 6, h: 7 },
  pie: { w: 4, h: 7 },
  table: { w: 12, h: 9 },
  histogram: { w: 6, h: 7 },
  gauge: { w: 3, h: 5 },
  map: { w: 8, h: 10 },
}

export const TEMP_PREFIX = "new:"
export const isNewTile = (id: string) => id.startsWith(TEMP_PREFIX)

let counter = 0
const tempId = () => `${TEMP_PREFIX}${++counter}`

export function tileForMetric(dashboardId: string, metric: MetricDefinition, sortOrder: number): TileItem {
  const widget = metric.defaultWidget
  const config: TileConfig = {
    metrics: [metric.id],
    widgetType: widget,
    splitBy: [...defaultSplitFor(metric)],
    comparePrevious: false,
    overrideFilters: null,
    display: {
      showLegend: true,
      legendPosition: "bottom",
      stacked: widget === "stacked-bar",
      showDataLabels: "endpoints",
      valueFormat: "auto",
      donut: true,
      area: false,
      sort: widget === "hbar" ? "desc" : "none",
    },
    ...(widget === "hbar" ? { topN: 20 } : {}),
  }

  return {
    id: tempId(),
    dashboardId,
    title: null, // null → the frontend uses metric_definition.name
    widgetType: widget,
    layout: {},
    config,
    sortOrder,
  }
}

const box = (item: GridItem) => ({ x: item.x, y: item.y, w: item.w, h: item.h })

export function placeAfter(
  layouts: Layouts,
  afterTileId: string | null,
  newTiles: readonly TileItem[],
  breakpoints: readonly Breakpoint[],
): Layouts {
  const out = {} as Layouts

  for (const bp of breakpoints) {
    const cols = COLS[bp]
    const existing = layouts[bp] ?? []
    const anchor = afterTileId ? existing.find((i) => i.i === afterTileId) : undefined

    const bottom = existing.reduce((n, i) => Math.max(n, i.y + i.h), 0)
    let x = anchor ? anchor.x + anchor.w : 0
    let y = anchor ? anchor.y : bottom
    let rowH = anchor ? anchor.h : 0

    const placed: GridItem[] = []
    for (const tile of newTiles) {
      const size = WIDGET_SIZE[tile.widgetType]
      const w = Math.min(size.w, cols)
      if (x + w > cols) {
        x = 0
        y += rowH
        rowH = 0
      }
      placed.push({ i: tile.id, x, y, w, h: size.h, static: false, minW: 2, minH: 2 })
      x += w
      rowH = Math.max(rowH, size.h)
    }

    const shiftFrom = anchor ? anchor.y + anchor.h : bottom
    const shiftBy = placed.reduce((n, i) => Math.max(n, i.y + i.h), shiftFrom) - shiftFrom

    out[bp] = [
      ...existing.map((i) => (i.y >= shiftFrom ? { ...i, y: i.y + shiftBy } : i)),
      ...placed,
    ]
  }

  return out
}

export function layoutOf(layouts: Layouts, tileId: string, breakpoints: readonly Breakpoint[]) {
  const out: Record<string, { x: number; y: number; w: number; h: number }> = {}
  for (const bp of breakpoints) {
    const item = layouts[bp]?.find((i) => i.i === tileId)
    if (item) out[bp] = box(item)
  }
  return out
}
